'use client';

import { useState, useMemo, useCallback, useRef } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Drawer,
  Text,
  Badge,
  BadgeSeverity,
  Tabs,
  Scroll,
  Button,
  MarkdownPreview,
  Spinner,
  IconButton,
  Tooltip,
  Menu,
} from '@terra/ui-design-system';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';
import {
  ReportFileStatusSchema,
  type FindingSeverityType,
} from '@terra/shared-schemas-orchestrator';
import {
  AlertCircleIcon,
  Tick01Icon,
  CheckmarkCircle02Icon,
  ArrowDown01Icon,
  Download01Icon,
  LinkSquare02Icon,
  File01Icon,
  Files01Icon,
  Key01Icon,
  Loading03Icon,
  FloppyDiskIcon,
  Settings01Icon,
  Delete02Icon,
  Cancel01Icon,
  FlashIcon,
} from '@terra/ui-icons';
import { toasterApi } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReportDetailsTab, type ReportDetailsTabRef } from './ReportDetailsTab';
import { ReportGenerateTab } from './ReportGenerateTab';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { getPublishedRelatedFindings } from '../../utils/get-published-related-findings';

interface ReportDetailsDrawerProps {
  report: ReportsReturnDto | null;
  isOpen: boolean;
  onClose: () => void;
}
interface Finding {
  title: string;
  description?: string;
  createdAt: string | Date;
  strategicNextSteps?: string;
  severity?: FindingSeverityType;
}

export function ReportDetailsDrawer({
  report,
  isOpen,
  onClose,
}: ReportDetailsDrawerProps) {
  const dateFormatter = useDateFormatter('en-US');
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState('overview');
  const [pdfOpeningState, setPdfOpeningState] = useState<
    Record<string, 'idle' | 'loading' | 'opened'>
  >({});
  // Details tab state
  const detailsTabRef = useRef<ReportDetailsTabRef>(null);
  const [isDetailsDirty, setIsDetailsDirty] = useState(false);
  const [isDetailsSaving, setIsDetailsSaving] = useState(false);
  // Fetch report files
  const {
    data: filesData,
    isLoading: isLoadingFiles,
    refetch: refetchFiles,
  } = useQuery({
    queryKey: ['report-files', report?.id],
    queryFn: async () => {
      if (!report?.id) return { files: [] };
      const result = await terraProxyApiClient.getReportFiles(report.id);

      return result;
    },
    enabled: !!report?.id && isOpen,
    refetchOnWindowFocus: false,
  });
  // Callback for PDF generation completion
  const handlePdfComplete = useCallback(() => {
    refetchFiles();
    setSelectedTab('files'); // Switch to files tab to show the new PDF
  }, [refetchFiles]);
  // Callback for report details update
  const handleReportUpdate = useCallback(() => {
    // Invalidate the report query to refetch updated data
    if (report?.id) {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  }, [queryClient, report?.id]);
  // Sort files by date (newest first)
  const sortedFiles = useMemo(() => {
    if (!filesData?.files) return [];

    return [...filesData.files].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();

      return dateB - dateA;
    });
  }, [filesData?.files]);
  const handleCopyPassword = useCallback(
    async (fileId: string) => {
      if (!report) return;

      try {
        const result = await terraProxyApiClient.getReportFilePassword(
          report.id,
          fileId,
        );

        if (result?.password) {
          await navigator.clipboard.writeText(result.password);
          toasterApi.success({
            title: 'Password copied',
            description: 'File password copied to clipboard.',
          });
        } else {
          toasterApi.error({
            title: 'No password available',
            description: 'This file is not password protected.',
          });
        }
      } catch (error) {
        console.error('Failed to get file password:', error);
        toasterApi.error({
          title: 'Failed to copy password',
          description: 'Could not retrieve file password.',
        });
      }
    },
    [report],
  );
  const [updatingFileId, setUpdatingFileId] = useState<string | null>(null);
  const handleStatusChange = useCallback(
    async (
      fileId: string,
      newStatus:
        | typeof ReportFileStatusSchema.enum.DRAFT
        | typeof ReportFileStatusSchema.enum.FINAL,
      currentStatus:
        | typeof ReportFileStatusSchema.enum.DRAFT
        | typeof ReportFileStatusSchema.enum.FINAL,
    ) => {
      if (!report || currentStatus === newStatus) return;
      setUpdatingFileId(fileId);

      try {
        await terraProxyApiClient.updateReportFileStatus(
          report.id,
          fileId,
          newStatus,
        );
        toasterApi.success({
          title: 'Status updated',
          description: `File status changed to ${newStatus}.`,
        });
        refetchFiles();
      } catch (error) {
        console.error('Failed to update file status:', error);
        toasterApi.error({
          title: 'Update failed',
          description: 'Failed to update file status. Please try again.',
        });
      } finally {
        setUpdatingFileId(null);
      }
    },
    [report, refetchFiles],
  );
  // Parse findings from the report
  const findings: Finding[] = useMemo(() => {
    if (!report) return [];
    const parsedFindings: Finding[] = [];

    getPublishedRelatedFindings({
      relatedFindings: report.relatedFindings,
    }).forEach((finding: unknown) => {
      try {
        const parsedFinding =
          typeof finding === 'string' ? JSON.parse(finding) : finding;

        parsedFindings.push({
          title:
            (
              parsedFinding as {
                title?: string;
              }
            ).title || 'Untitled Finding',
          description: (
            parsedFinding as {
              description?: string;
            }
          ).description,
          createdAt:
            (
              parsedFinding as {
                createdAt?: string;
              }
            ).createdAt || report.createdAt,
          strategicNextSteps: (
            parsedFinding as {
              strategicNextSteps?: string;
            }
          ).strategicNextSteps,
          severity: (
            parsedFinding as {
              severity?: FindingSeverityType;
            }
          ).severity,
        });
      } catch {
        parsedFindings.push({
          title: 'Finding',
          description: String(finding),
          createdAt: report.createdAt,
        });
      }
    });

    return parsedFindings;
  }, [report]);
  const hasStrategicNextSteps = useMemo(
    () => findings.some((finding) => finding.strategicNextSteps),
    [findings],
  );
  const formatDate = (date: string | Date) => {
    const parsedDate = date instanceof Date ? date : new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return 'Unknown date';
    }

    return dateFormatter.formatDateOnly(parsedDate);
  };

  const formatDateTime = (date: string | Date) => {
    const parsedDate = date instanceof Date ? date : new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return 'Unknown date';
    }

    return dateFormatter.format(parsedDate);
  };

  if (!report) return null;
  const reportWithDates = report as ReportsReturnDto & {
    reportDate?: string | Date | null;
  };
  const reportDateLabel = reportWithDates.reportDate
    ? formatDate(reportWithDates.reportDate)
    : 'Not set';

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
    >
      <Drawer.Backdrop />
      <Drawer.Positioner w="55vw" maxW="700px">
        <Drawer.Content>
          <Drawer.Header>
            <HStack justifyContent="space-between" w="full" gap={2}>
              <HStack gap={2} alignItems="start" flex="1" minW="0">
                <File01Icon size={16} />
                <VStack alignItems="start" gap={1} flex="1" minW="0">
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    w="full"
                  >
                    {report.title}
                  </Text>
                  <HStack gap={1.5} flexWrap="wrap">
                    {hasStrategicNextSteps && (
                      <Badge size="sm" variant="emphasis">
                        Next Steps
                      </Badge>
                    )}
                    <Text fontSize="xs" color="fg.muted">
                      Created {formatDate(report.createdAt)}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      •
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      Updated {formatDate(report.updatedAt)}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>
              <Drawer.CloseTrigger asChild>
                <IconButton size="xs" variant="ghost" aria-label="Close">
                  <Cancel01Icon size={14} />
                </IconButton>
              </Drawer.CloseTrigger>
            </HStack>
          </Drawer.Header>

          <Drawer.Body>
            <Tabs.Root
              value={selectedTab}
              onValueChange={(details) => setSelectedTab(details.value)}
              size="sm"
              w="full"
              h="full"
              display="flex"
              flexDirection="column"
              lazyMount
              unmountOnExit
            >
              <Tabs.List>
                <Tabs.Trigger value="overview">
                  <HStack gap={1}>
                    <File01Icon size={14} />
                    <Text>Overview</Text>
                  </HStack>
                </Tabs.Trigger>
                <Tabs.Trigger value="details">
                  <HStack gap={1}>
                    <Settings01Icon size={14} />
                    <Text>Details</Text>
                    {isDetailsDirty && (
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg="orange.500"
                        title="Unsaved changes"
                      />
                    )}
                  </HStack>
                </Tabs.Trigger>
                <Tabs.Trigger value="generate">
                  <HStack gap={1}>
                    <FlashIcon size={14} />
                    <Text>Generate</Text>
                  </HStack>
                </Tabs.Trigger>
                <Tabs.Trigger value="files">
                  <HStack gap={1}>
                    <Files01Icon size={14} />
                    <Text>Files ({sortedFiles.length})</Text>
                  </HStack>
                </Tabs.Trigger>
                <Tabs.Indicator />
              </Tabs.List>

              {/* Overview Tab - Findings Summary */}
              <Tabs.Content value="overview" flex="1" overflow="hidden" pt={2}>
                <Scroll orientation="vertical" showScrollbar={true} flex="1">
                  <VStack gap={2} w="full" pb={2}>
                    {findings.length === 0 ? (
                      <Text color="fg.muted" fontSize="sm">
                        No findings in this report
                      </Text>
                    ) : (
                      findings.map((finding, index) => (
                        <Box
                          key={index}
                          w="full"
                          py={2}
                          borderBottomWidth={
                            index < findings.length - 1 ? '1px' : '0'
                          }
                        >
                          <VStack gap={2} alignItems="flex-start">
                            <HStack
                              gap={2}
                              w="full"
                              justifyContent="space-between"
                            >
                              <Text fontWeight="medium" fontSize="sm">
                                {finding.title}
                              </Text>
                              {finding.severity && (
                                <BadgeSeverity
                                  severity={finding.severity}
                                  size="sm"
                                >
                                  {finding.severity}
                                </BadgeSeverity>
                              )}
                            </HStack>

                            {finding.description && (
                              <Box w="full">
                                <MarkdownPreview source={finding.description} />
                              </Box>
                            )}

                            {finding.strategicNextSteps && (
                              <Box
                                w="full"
                                p={2}
                                bg="bg.subtle"
                                borderRadius="md"
                              >
                                <VStack gap={1} alignItems="flex-start">
                                  <Text
                                    fontSize="xs"
                                    fontWeight="medium"
                                    color="fg.muted"
                                  >
                                    Next Steps
                                  </Text>
                                  <MarkdownPreview
                                    source={finding.strategicNextSteps}
                                  />
                                </VStack>
                              </Box>
                            )}
                          </VStack>
                        </Box>
                      ))
                    )}
                  </VStack>
                </Scroll>
              </Tabs.Content>

              {/* Details Tab - Editable report metadata */}
              <Tabs.Content value="details" flex="1" overflow="hidden" pt={2}>
                <Scroll orientation="vertical" showScrollbar={true} flex="1">
                  <Box w="full" pb={2}>
                    <ReportDetailsTab
                      ref={detailsTabRef}
                      report={report}
                      onUpdate={handleReportUpdate}
                      onDirtyChange={setIsDetailsDirty}
                      onSavingChange={setIsDetailsSaving}
                    />
                  </Box>
                </Scroll>
              </Tabs.Content>

              {/* Generate Tab - PDF Generation */}
              <Tabs.Content value="generate" flex="1" overflow="hidden" pt={2}>
                <Scroll orientation="vertical" showScrollbar={true} flex="1">
                  <Box w="full" pb={2}>
                    <ReportGenerateTab
                      report={report}
                      onComplete={handlePdfComplete}
                    />
                  </Box>
                </Scroll>
              </Tabs.Content>

              {/* Files Tab */}
              <Tabs.Content value="files" flex="1" overflow="hidden" pt={2}>
                <Scroll orientation="vertical" showScrollbar={true} flex="1">
                  <VStack gap={2} w="full" pb={2}>
                    {isLoadingFiles ? (
                      <Box
                        w="full"
                        display="flex"
                        justifyContent="center"
                        py={4}
                      >
                        <Spinner size="sm" />
                      </Box>
                    ) : sortedFiles.length === 0 ? (
                      <Box
                        w="full"
                        py={4}
                        display="flex"
                        justifyContent="center"
                      >
                        <VStack gap={1} alignItems="center">
                          <Box color="fg.muted">
                            <Files01Icon size={24} />
                          </Box>
                          <Text color="fg.muted" fontSize="xs">
                            No PDF files generated yet
                          </Text>
                          <Text color="fg.muted" fontSize="xs">
                            Use the Generate tab to create a PDF report
                          </Text>
                        </VStack>
                      </Box>
                    ) : (
                      <VStack
                        gap={0}
                        w="full"
                        divideY="1px"
                        borderWidth="1px"
                        borderColor="border.default"
                        borderRadius="md"
                      >
                        {sortedFiles.map((file) => (
                          <HStack
                            key={file.id}
                            gap={3}
                            w="full"
                            p={2.5}
                            justifyContent="space-between"
                            alignItems="center"
                            _hover={{ bg: 'bg.subtle' }}
                          >
                            <HStack
                              gap={2}
                              flex="1"
                              minW="0"
                              alignItems="flex-start"
                            >
                              <Box color="fg.muted">
                                <File01Icon size={14} />
                              </Box>
                              <VStack
                                gap={1}
                                alignItems="flex-start"
                                flex="1"
                                minW="0"
                              >
                                <Text
                                  fontSize="sm"
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                  w="full"
                                >
                                  {file.name}
                                </Text>
                                <Text fontSize="xs" color="fg.muted">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </Text>
                                <VStack gap={0.5} alignItems="flex-start">
                                  <Text fontSize="xs" color="fg.muted">
                                    Created {formatDateTime(file.createdAt)}
                                  </Text>
                                  <Text fontSize="xs" color="fg.muted">
                                    Report date {reportDateLabel}
                                  </Text>
                                </VStack>
                              </VStack>
                            </HStack>

                            <HStack gap={2} alignItems="center" flexShrink={0}>
                              <Menu.Root positioning={{ strategy: 'fixed' }}>
                                <Menu.Trigger asChild>
                                  <Box
                                    as="button"
                                    display="inline-flex"
                                    alignItems="center"
                                    gap={1}
                                    cursor={
                                      updatingFileId === file.id
                                        ? 'not-allowed'
                                        : 'pointer'
                                    }
                                    opacity={
                                      updatingFileId === file.id ? 0.6 : 1
                                    }
                                    pointerEvents={
                                      updatingFileId === file.id
                                        ? 'none'
                                        : 'auto'
                                    }
                                    bg="transparent"
                                    border="none"
                                    p={0}
                                  >
                                    <Badge
                                      size="sm"
                                      variant="emphasis"
                                      colorScheme={
                                        file.status ===
                                        ReportFileStatusSchema.enum.FINAL
                                          ? 'green'
                                          : undefined
                                      }
                                      icon={
                                        file.status ===
                                        ReportFileStatusSchema.enum.FINAL ? (
                                          <CheckmarkCircle02Icon size={10} />
                                        ) : (
                                          <File01Icon size={10} />
                                        )
                                      }
                                    >
                                      {file.status}
                                    </Badge>
                                    <Box color="fg.muted">
                                      <ArrowDown01Icon size={12} />
                                    </Box>
                                  </Box>
                                </Menu.Trigger>
                                <Menu.Positioner>
                                  <Menu.Content minW="120px" py="1">
                                    <Menu.Item
                                      value={ReportFileStatusSchema.enum.DRAFT}
                                      fontSize="xs"
                                      py="1"
                                      px="2"
                                      disabled={
                                        file.status ===
                                        ReportFileStatusSchema.enum.DRAFT
                                      }
                                      onClick={() =>
                                        handleStatusChange(
                                          file.id,
                                          ReportFileStatusSchema.enum.DRAFT,
                                          file.status,
                                        )
                                      }
                                    >
                                      <HStack gap="1.5">
                                        <File01Icon size={12} />
                                        <Text fontSize="xs">
                                          {ReportFileStatusSchema.enum.DRAFT}
                                        </Text>
                                      </HStack>
                                    </Menu.Item>
                                    <Menu.Item
                                      value={ReportFileStatusSchema.enum.FINAL}
                                      fontSize="xs"
                                      py="1"
                                      px="2"
                                      disabled={
                                        file.status ===
                                        ReportFileStatusSchema.enum.FINAL
                                      }
                                      onClick={() =>
                                        handleStatusChange(
                                          file.id,
                                          ReportFileStatusSchema.enum.FINAL,
                                          file.status,
                                        )
                                      }
                                    >
                                      <HStack gap="1.5">
                                        <CheckmarkCircle02Icon size={12} />
                                        <Text fontSize="xs">
                                          {ReportFileStatusSchema.enum.FINAL}
                                        </Text>
                                      </HStack>
                                    </Menu.Item>
                                  </Menu.Content>
                                </Menu.Positioner>
                              </Menu.Root>

                              <HStack gap={1}>
                                {file.downloadUrl && (
                                  <>
                                    <Tooltip
                                      title={
                                        pdfOpeningState[file.id] === 'opened'
                                          ? 'Opened in new window'
                                          : 'Open in new window'
                                      }
                                    >
                                      <IconButton
                                        size="sm"
                                        variant="ghost"
                                        disabled={
                                          pdfOpeningState[file.id] === 'loading'
                                        }
                                        onClick={async () => {
                                          if (file.downloadUrl) {
                                            setPdfOpeningState((prev) => ({
                                              ...prev,
                                              [file.id]: 'loading',
                                            }));

                                            try {
                                              await window.electronAPI.pdf.openInWindow(
                                                file.downloadUrl,
                                                file.name,
                                              );
                                              setPdfOpeningState((prev) => ({
                                                ...prev,
                                                [file.id]: 'opened',
                                              }));
                                              // Reset to idle after 3 seconds
                                              setTimeout(() => {
                                                setPdfOpeningState((prev) => ({
                                                  ...prev,
                                                  [file.id]: 'idle',
                                                }));
                                              }, 3000);
                                            } catch (error) {
                                              console.error(
                                                'Failed to open PDF:',
                                                error,
                                              );
                                              setPdfOpeningState((prev) => ({
                                                ...prev,
                                                [file.id]: 'idle',
                                              }));
                                              toasterApi.error({
                                                title: 'Error',
                                                description:
                                                  'Failed to open PDF',
                                              });
                                            }
                                          }
                                        }}
                                        aria-label="Open in new window"
                                      >
                                        {pdfOpeningState[file.id] ===
                                        'loading' ? (
                                          <Loading03Icon
                                            size={14}
                                            style={{
                                              animation:
                                                'spin 1s linear infinite',
                                            }}
                                          />
                                        ) : pdfOpeningState[file.id] ===
                                          'opened' ? (
                                          <Tick01Icon size={14} />
                                        ) : (
                                          <LinkSquare02Icon size={14} />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download">
                                      <IconButton
                                        size="sm"
                                        variant="ghost"
                                        onClick={async () => {
                                          if (file.downloadUrl) {
                                            try {
                                              await window.electronAPI.net.downloadFile(
                                                file.downloadUrl,
                                                file.name,
                                              );
                                            } catch (error) {
                                              console.error(
                                                'Download failed:',
                                                error,
                                              );
                                              toasterApi.error({
                                                title: 'Error',
                                                description:
                                                  'Failed to download file',
                                              });
                                            }
                                          }
                                        }}
                                        aria-label="Download file"
                                      >
                                        <Download01Icon size={14} />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                {file.hasPassword && (
                                  <Tooltip title="Copy Password">
                                    <IconButton
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        handleCopyPassword(file.id)
                                      }
                                      aria-label="Copy password"
                                    >
                                      <Key01Icon size={14} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={async () => {
                                      try {
                                        await terraProxyApiClient.deleteReportFile(
                                          report.id,
                                          file.id,
                                        );
                                        toasterApi.success({
                                          title: 'File deleted',
                                          description:
                                            'The file has been deleted.',
                                        });
                                        refetchFiles();
                                      } catch (error) {
                                        console.error(
                                          'Failed to delete file:',
                                          error,
                                        );
                                        toasterApi.error({
                                          title: 'Failed to delete file',
                                          description: 'An error occurred.',
                                        });
                                      }
                                    }}
                                    aria-label="Delete file"
                                  >
                                    <Delete02Icon size={14} />
                                  </IconButton>
                                </Tooltip>
                              </HStack>
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </Scroll>
              </Tabs.Content>
            </Tabs.Root>
          </Drawer.Body>

          <Drawer.Footer>
            <HStack gap={2} w="full" justifyContent="space-between">
              {selectedTab === 'details' && isDetailsDirty ? (
                <HStack gap={1.5} color="orange.fg">
                  <AlertCircleIcon size={14} />
                  <Text fontSize="xs">Unsaved changes</Text>
                </HStack>
              ) : (
                <Box />
              )}

              <HStack gap={2}>
                <Drawer.CloseTrigger asChild>
                  <Button variant="outline" size="sm">
                    Close
                  </Button>
                </Drawer.CloseTrigger>
                {selectedTab === 'details' && (
                  <Button
                    size="sm"
                    onClick={() => detailsTabRef.current?.save()}
                    loading={isDetailsSaving}
                    disabled={isDetailsSaving || !isDetailsDirty}
                  >
                    <FloppyDiskIcon size={14} />
                    Save Changes
                  </Button>
                )}
              </HStack>
            </HStack>
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
